import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronDown, Clock, Database, Radio, Unlink } from 'lucide-react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ResourceType } from '@cotera/api';
import { Icon } from '@cotera/client/app/components/ui';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@cotera/client/v0/components/ui/accordion';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  RESOURCE_LIST_CELL_TEXT,
  RESOURCE_LIST_CELL_TEXT_SIZE,
} from '../resources/list/resource-list-types';
import type { WorkflowSummary } from '../workflow/use-workflow-summaries';
import {
  WorkflowRow,
  TriggerStatusBars,
  TriggerStatusBarsSkeleton,
  LIST_ROW_ACTIONS_CELL,
  LIST_ROW_CELL,
  LIST_ROW_CHEVRON_GUTTER,
  LIST_ROW_NAME_INSET_AFTER_GUTTER,
  NestedRowGutter,
  LIST_ROW_STATUS_CELL,
  type WorkflowRowRef,
} from '../workflow/workflow-list-row';
import { resourceTypeIconRingClassName } from '../resources/type/resource-type-colors';
import {
  formatNextRun,
  getNextRun,
  humanReadableSchedule,
} from './schedule-utils';
import {
  useTriggerWorkflows,
  type TriggerSummary,
  type WorkflowOption,
} from './hooks';
import type { TriggerWorkflowBar } from './hooks';
import type { TriggerListItem } from './triggers-list.model';
import type { ItemAction } from '../resources/list/resource-list-view';
import { RowActionsMenu } from '../resources/list/row-actions-menu';
import {
  WebhookProviderIcon,
  useWebhookProviderCatalog,
} from '../webhook-providers';
import { webhookTriggerDescription } from './webhook-trigger-description';
import { datasetTriggerDescription } from './dataset-trigger-description';

export type { WorkflowRowRef };

/**
 * One-line note inside an expanded group, standing in for the rows that aren't
 * there yet while they load. Carries the rail and the name inset so it sits in
 * the column those rows will occupy.
 */
const NestedNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="relative flex w-full items-center after:absolute after:bottom-0 after:left-7 after:right-0 after:h-px after:bg-border/40">
    <NestedRowGutter />
    <span
      className={cn(
        'py-3.5 text-xs text-muted-foreground',
        LIST_ROW_NAME_INSET_AFTER_GUTTER
      )}
    >
      {children}
    </span>
  </div>
);

/**
 * The empty state of an expanded trigger.
 *
 * A trigger with nothing attached does nothing when it fires, so the row that
 * says so is the natural place to fix it. The `…` menu carries the same action,
 * but only for someone who thinks to open it — and this is the one row where
 * there is nothing else to do.
 */
const NestedEmptyState: React.FC<{ onAddWorkflow?: () => void }> = ({
  onAddWorkflow,
}) => (
  <div className="relative flex w-full items-center after:absolute after:bottom-0 after:left-7 after:right-0 after:h-px after:bg-border/40">
    <NestedRowGutter />
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 py-3.5 text-xs',
        LIST_ROW_NAME_INSET_AFTER_GUTTER
      )}
    >
      <span className="text-muted-foreground">No workflows attached yet.</span>
      {onAddWorkflow !== undefined ? (
        // Set at the note's own size, not a button beside it: the row is one
        // sentence with its remedy at the end. A dashed outline reads as a slot
        // waiting to be filled — the same shape the email trigger card uses for
        // its pending address — and stays visible without hover, since a control
        // nobody can see is no better than the `…` menu this shortcuts.
        <button
          type="button"
          onClick={(event) => {
            // The row sits inside the accordion; without this the click also
            // reaches the group and collapses the trigger being fixed.
            event.stopPropagation();
            onAddWorkflow();
          }}
          className="rounded-sm border border-dashed border-border px-1.5 py-0.5 font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-secondary hover:text-foreground"
        >
          Add workflow
        </button>
      ) : null}
    </div>
  </div>
);

/**
 * The header row of a group in the Automations list — one for each trigger, plus
 * the synthetic "Standalone" group. Shared so the two are the same row by
 * construction rather than by two sets of matching classes: same caret gutter,
 * same icon bubble, same name stack, same three cells.
 *
 * `actions` is what separates a real trigger from a bucket: given actions the
 * leading icon doubles as the row's `…` menu (the Library pattern), without them
 * it is just an icon — a Standalone group has nothing to rename, move or archive.
 */
const GroupHeaderRow: React.FC<{
  isOpen: boolean;
  isSelected?: boolean;
  icon: React.ReactNode;
  name: string;
  /** Second line. Omitted by trigger rows, whose description *is* the name. */
  subtitle?: string;
  /**
   * The Status cell's contents — for a trigger, its {@link TriggerStatusBars}: one
   * bar per bound workflow, coloured by that workflow's latest run. It stays put
   * whether the group is open or closed, because it is now the *only* place the
   * list shows run status — the workflow rows nested under it no longer restate
   * it, so there is no longer a "print it twice down one column" problem to dodge
   * by hiding it when expanded.
   *
   * `undefined` renders an em-dash — a bucket with nothing bound has no health of
   * its own.
   */
  statusContent?: React.ReactNode;
  /** Omitted for a group that has no schedule to speak of, leaving the cell blank. */
  nextRunLabel?: string;
  actions?: ItemAction[];
  onOpenActions?: () => void;
}> = ({
  isOpen,
  isSelected = false,
  icon,
  name,
  subtitle,
  statusContent,
  nextRunLabel,
  actions,
  onOpenActions,
}) => (
  /*
   * `sticky` pins the header while its own group is on screen, so scrolling a long
   * group never leaves you reading workflows with no idea whose they are. It pins
   * *below* the list's own sticky header via the height that publishes as
   * `--list-sticky-header-h`, and `AccordionItem` bounds it, so the header
   * releases once its group scrolls past. Sticky also establishes the containing
   * block the trigger's stretched `before:inset-0` hit area resolves against —
   * the job `relative` used to do here.
   *
   * The background is what makes pinning legible: rows must pass *behind* the
   * header, not through it. `bg-background` is the page colour, so it changes
   * nothing visually while unpinned — and the selection tint has to live here
   * rather than on the item, or this opaque layer would paint over it.
   */
  <AccordionPrimitive.Header
    className={cn(
      'group/row sticky top-[var(--list-sticky-header-h,0px)] z-[2] isolate flex w-full min-w-0 items-center',
      /*
       * The element's own background stays `bg-background`: opaque and square, so
       * rows pass behind this header while it's pinned. Both tints move to a
       * rounded pseudo instead — matching the header `+` button's `rounded-sm`,
       * the way the child rows' hover does. `isolate` plus `-z-10` keeps it above
       * that opaque layer but behind the row's own content.
       *
       * This also fixes selection under sticky: painting `bg-secondary/60`
       * directly on the element made the pinned header translucent, so rows
       * showed through as they scrolled under it.
       */
      'bg-background before:absolute before:inset-0 before:-z-10 before:rounded-sm before:transition-colors before:content-[""]',
      isSelected ? 'before:bg-secondary/60' : 'hover:before:bg-secondary/60',
      // Only when open, where it divides the header from its own rows. Closed,
      // the spacing between groups is the separator.
      isOpen && 'border-b border-border/40'
    )}
  >
    {/*
     * The disclosure caret is decorative — the trigger below stretches its hit
     * area over the whole row (`before:inset-0`), so clicking the caret, the name
     * or the run cells all toggle the row, and the row still has a single
     * accessible control.
     */}
    <span
      className={cn(
        LIST_ROW_CHEVRON_GUTTER,
        'flex h-8 items-center justify-center transition-colors',
        // The shape already changes; this makes the weight change too.
        isOpen ? 'text-slate-600' : 'text-muted-foreground/60'
      )}
      aria-hidden="true"
    >
      <ChevronDown
        className={cn(
          'size-3.5 transition-transform duration-200',
          !isOpen && '-rotate-90'
        )}
      />
    </span>
    {icon}
    <AccordionPrimitive.Trigger
      data-slot="accordion-trigger"
      className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 pr-3 text-left outline-none before:absolute before:inset-0 before:content-[''] focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-800">{name}</p>
        {subtitle !== undefined ? (
          <p className="mt-0.5 truncate text-xs text-muted-foreground/70">
            {subtitle}
          </p>
        ) : null}
      </div>
    </AccordionPrimitive.Trigger>
    <div className={cn(LIST_ROW_STATUS_CELL, 'pointer-events-none')}>
      {statusContent ?? (
        <span
          className={cn(
            RESOURCE_LIST_CELL_TEXT_SIZE,
            'text-muted-foreground/50'
          )}
        >
          —
        </span>
      )}
    </div>
    {/* Last run stays empty on a group header: a trigger *fires*, it doesn't
        run — runs belong to the workflows nested under it, which report their
        own. Held as a cell so the columns stay aligned. */}
    <div className={LIST_ROW_CELL} aria-hidden="true" />
    <div className={cn(LIST_ROW_CELL, 'pointer-events-none')}>
      {nextRunLabel !== undefined ? (
        <span className={cn(RESOURCE_LIST_CELL_TEXT, 'whitespace-nowrap')}>
          {nextRunLabel}
        </span>
      ) : null}
    </div>
    {/* Above the trigger's stretched hit area, so the menu takes the click.
        `z-[1]` (not `z-10`) — it only has to beat the trigger's z-auto
        `::before`, and must stay under the sticky list header (`z-10`). */}
    <div
      className={cn(
        LIST_ROW_ACTIONS_CELL,
        'relative z-[1] flex items-center justify-center opacity-0 transition-opacity focus-within:opacity-100 group-hover/row:opacity-100 has-[button[data-state=open]]:opacity-100'
      )}
    >
      <RowActionsMenu
        actions={actions ?? []}
        onOpen={onOpenActions}
        label={`Actions for ${name}`}
      />
    </div>
  </AccordionPrimitive.Header>
);

export const TriggerAccordionCard: React.FC<{
  trigger: TriggerListItem;
  workflowCount: number;
  lastFiredAt: Date | null;
  /**
   * Per-workflow rollup for the status bars — one entry per bound workflow, with
   * its latest run status and 24h failure counts. From the batched summaries, so
   * a collapsed row can draw its bars without expanding.
   */
  workflows: TriggerWorkflowBar[];
  /** The batched summary hasn't landed yet — shimmer the status cell until it does. */
  summaryPending: boolean;
  isOpen: boolean;
  isSelected: boolean;
  /** Name of the watched dataset when the trigger is a dataset watcher. */
  datasetName?: string | null;
  onFocusTrigger: () => void;
  /** Attaches a workflow to this trigger, offered from its empty state. */
  onAddWorkflow?: () => void;
  workflowRowActions?: (workflow: WorkflowRowRef) => ItemAction[];
  /**
   * Actions menu for the trigger itself (Rename / Move / Archive). Receives the
   * attached workflow count so the archive confirm can warn when detaching
   * would leave workflows unattached.
   */
  triggerActions?: (
    trigger: TriggerListItem,
    workflowCount: number
  ) => ItemAction[];
}> = ({
  trigger,
  workflowCount,
  lastFiredAt,
  workflows: workflowBars,
  summaryPending,
  isOpen,
  isSelected,
  datasetName = null,
  onFocusTrigger,
  onAddWorkflow,
  workflowRowActions,
  triggerActions,
}) => {
  const { workflows, isLoading } = useTriggerWorkflows(trigger.id, isOpen);

  // `workflowCount` comes from the batched trigger-summaries rollup, which can
  // lag a bind by a refresh cycle — so attaching a workflow left the row still
  // reading "No workflows" and dimmed as dormant, contradicting the workflow
  // now listed right below it. An expanded card has the authoritative list, so
  // prefer it, and remember it across a collapse (which drops the list). The
  // remembered value is discarded the moment the rollup reports a count of its
  // own, so an external bind/unbind still wins.
  const [loadedCount, setLoadedCount] = useState<number | null>(null);
  const lastSummaryCount = useRef(workflowCount);
  useEffect(() => {
    if (lastSummaryCount.current !== workflowCount) {
      lastSummaryCount.current = workflowCount;
      setLoadedCount(null);
    }
  }, [workflowCount]);
  useEffect(() => {
    if (isOpen && !isLoading) {
      setLoadedCount(workflows.length);
    }
  }, [isOpen, isLoading, workflows.length]);
  const attachedCount = loadedCount ?? workflowCount;

  const isWebhook = trigger.kind === 'webhook';
  const isEvent = trigger.kind === 'event';
  const isDataset = trigger.kind === 'dataset';

  // One cached query for the whole page — every row asking for it shares the
  // same entry, and it is what turns a stored `github`/`pull_request.opened`
  // pair into "GitHub · Pull request opened".
  const { providers: webhookProviders } = useWebhookProviderCatalog();

  // Shared with the creation wizard, so the name stored for a dataset trigger
  // is the line the row will show for it.
  const datasetWatchLabel = datasetTriggerDescription({
    datasetName,
    criterion: trigger.datasetCriterion,
    runMode: trigger.datasetRunMode,
  });

  /*
   * What the trigger *does* is the row's identity — the free-form name is gone.
   * It was usually a restatement of this ("Every Sunday at 3:00 PM" named
   * "Every Sunday at 3:00 PM"), and when it wasn't it hid the one fact the row
   * exists to show. Which also retires the "Untitled …" fallbacks: an unnamed
   * trigger is no longer a row with nothing to say.
   *
   * No kind prefix. The icon beside it is the kind — an ochre clock, the source
   * app's logo, a radio, a database — so "Schedule · " only repeated it. A
   * webhook keeps its provider ("GitHub · Push") because that is a specific
   * identity rather than a category, and because the same string names the
   * trigger in the Library and in search, where no logo is drawn.
   */
  // The server stores the canonical, self-describing label in `name` — the same
  // string the Library and search show, and the only one that carries the
  // identifying config a client-side derivation can't see (a webhook's repo, a
  // cron's timezone). Render that; the client-side derivations below remain only
  // as a fallback for a row whose label hasn't been backfilled. Dataset triggers
  // keep their watch-config line.
  const derivedFallback = isWebhook
    ? webhookTriggerDescription(
        {
          providerKey: trigger.webhookProviderKey,
          eventKey: trigger.webhookEventKey,
          inboundEmailAddress: trigger.inboundEmailAddress,
        },
        webhookProviders
      )
    : isEvent
    ? trigger.eventName !== null && trigger.eventName !== ''
      ? trigger.eventName
      : 'Event'
    : trigger.schedule !== ''
    ? humanReadableSchedule(trigger.schedule)
    : 'Schedule';

  const description = isDataset
    ? datasetWatchLabel
    : trigger.name.trim() !== ''
    ? trigger.name
    : derivedFallback;

  // Only cron triggers have a computable next run; the rest fire on an inbound
  // signal, so say so rather than showing an empty cell.
  const nextRunLabel =
    !isWebhook && !isEvent && !isDataset && trigger.schedule !== ''
      ? formatNextRun(getNextRun(trigger.schedule))
      : 'Event-driven';

  // Nothing attached and never fired → not doing any work yet; dim it so active
  // triggers stand out. Held off while the summary is still loading, so a row
  // doesn't flash dormant before its rollup lands.
  const dormant =
    !summaryPending && attachedCount === 0 && lastFiredAt === null;
  const actions = triggerActions?.(trigger, attachedCount);

  // The leading icon carries the card's actions menu (Library pattern), so it
  // lives OUTSIDE the accordion trigger button — a menu button can't be nested
  // inside another button. The bubble takes the palette of whatever the trigger
  // is *about* (a dataset watcher is dataset-blue, a workflow-event listener is
  // workflow-teal), so its color matches that resource everywhere else in the
  // app; time/app-driven triggers keep the trigger ochre.
  const kindIcon = isWebhook ? (
    // Show the source app's logo for a connected provider; the generic webhook
    // icon for a custom (`generic`/unknown) webhook.
    <WebhookProviderIcon
      providerKey={trigger.webhookProviderKey ?? 'generic'}
      className="h-4 w-4 shrink-0"
    />
  ) : isEvent ? (
    <Radio className="h-4 w-4 shrink-0" strokeWidth={1.75} />
  ) : isDataset ? (
    <Database className="h-4 w-4 shrink-0" strokeWidth={1.75} />
  ) : (
    <Clock className="h-4 w-4 shrink-0" strokeWidth={1.75} />
  );
  const iconResourceType = isDataset
    ? ResourceType.Dataset
    : isEvent
    ? ResourceType.Workflow
    : ResourceType.WorkflowTrigger;
  const triggerIcon = (
    <span
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm',
        resourceTypeIconRingClassName(iconResourceType),
        // Quiet the bubble on a trigger that isn't doing anything, and leave the
        // name at full contrast so the row stays readable.
        dormant && 'opacity-60'
      )}
    >
      {kindIcon}
    </span>
  );

  return (
    <AccordionItem
      value={trigger.id}
      onFocusCapture={onFocusTrigger}
      onMouseEnter={onFocusTrigger}
      className="border-b-0"
    >
      <GroupHeaderRow
        isOpen={isOpen}
        isSelected={isSelected}
        icon={triggerIcon}
        name={description}
        statusContent={
          summaryPending ? (
            <TriggerStatusBarsSkeleton />
          ) : (
            <TriggerStatusBars workflows={workflowBars} />
          )
        }
        nextRunLabel={nextRunLabel}
        actions={actions ?? []}
        onOpenActions={onFocusTrigger}
      />
      <AccordionContent className="pb-0">
        {/*
         * Workflows render flat, sharing the trigger row's left edge and column
         * geometry, so an expanded trigger reads as more lines of the same table
         * rather than a nested card stack (matches the Library list).
         */}
        <div className="flex flex-col">
          {isLoading ? (
            <NestedNote>Loading…</NestedNote>
          ) : workflows.length === 0 ? (
            <NestedEmptyState onAddWorkflow={onAddWorkflow} />
          ) : (
            workflows.map((wf) => (
              <WorkflowRow
                key={wf.workflowId}
                workflowId={wf.workflowId}
                name={wf.name}
                lastRunAt={wf.lastRunAt}
                bindingOff={wf.bindingEnabled === false}
                actions={workflowRowActions?.({
                  workflowId: wf.workflowId,
                  name: wf.name,
                })}
              />
            ))
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

/**
 * Accordion id for the synthetic "Standalone" group. Namespaced so it can never
 * collide with a real trigger id in `openIds`.
 */
const STANDALONE_GROUP_ID = '__standalone__';

/**
 * Workflows with no trigger, rendered as one more group in the same accordion.
 *
 * A grouped list has to put items with no group value *somewhere*, and the answer
 * every grouped list converges on is a group of their own, last, built exactly
 * like the real ones — not a section with its own typography. So this shares
 * `GroupHeaderRow` with the trigger cards and differs only where it honestly
 * must: a broken-link bubble, no actions menu (there's nothing here to rename or
 * archive), and no next run, because a bucket has no schedule of its own.
 *
 * These workflows are valid — they run from Coco, the API, or another workflow —
 * so they belong in the default view. The group starts collapsed to say so
 * without spending the room.
 */
const StandaloneWorkflowsCard: React.FC<{
  workflows: WorkflowOption[];
  summaries: Map<string, WorkflowSummary>;
  isOpen: boolean;
  workflowRowActions?: (workflow: WorkflowRowRef) => ItemAction[];
}> = ({ workflows, summaries, isOpen, workflowRowActions }) => (
  <AccordionItem value={STANDALONE_GROUP_ID} className="border-b-0">
    <GroupHeaderRow
      isOpen={isOpen}
      icon={
        // A broken link: this group is the workflows with no trigger bound, and
        // that is what it draws. Same stroke weight and size as its neighbours —
        // the clock, the database, the radio — so the column reads as one set.
        // Neutral grey rather than red or amber: unattached is a fact about these
        // workflows, not a fault in them.
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-muted text-slate-500">
          <Unlink className="h-4 w-4 shrink-0" strokeWidth={1.75} />
        </span>
      }
      name="Standalone"
      subtitle="Run from Coco, the API, or another workflow"
      // Same bar strip as a trigger, one per standalone workflow, coloured by its
      // last run. These have no shared trigger to roll up 24h failure counts
      // from, so the bars carry status only — the hover shows the last-run state,
      // not a percentage.
      statusContent={
        <TriggerStatusBars
          workflows={workflows.map((w) => ({
            workflowId: w.id,
            name: w.name,
            lastRunStatus: summaries.get(w.id)?.lastRunStatus ?? null,
            failedRuns: 0,
            totalRuns: 0,
          }))}
        />
      }
    />
    <AccordionContent className="pb-0">
      <div className="flex flex-col">
        {workflows.map((w) => (
          <WorkflowRow
            key={w.id}
            workflowId={w.id}
            name={w.name}
            lastRunAt={summaries.get(w.id)?.lastRunAt ?? null}
            actions={workflowRowActions?.({
              workflowId: w.id,
              name: w.name,
            })}
          />
        ))}
      </div>
    </AccordionContent>
  </AccordionItem>
);

/**
 * Group-by-trigger view. Triggers render as one flat list — ordered scheduled,
 * from-an-app, workflow-event, dataset-change — with their workflows nested
 * underneath, matching the Library's single-table look; the header chips narrow
 * it by kind.
 *
 * Workflows with no trigger are the last group in the same accordion — see
 * {@link StandaloneWorkflowsCard}.
 */
export const TriggersAccordionView: React.FC<{
  triggers: TriggerListItem[];
  onNewTrigger: () => void;
  onNewWorkflow: () => void;
  /** Dataset id -> name, for the dataset triggers' "watches X" subtitle. */
  datasetNameById?: Map<string, string>;
  /** Per-workflow summaries, for the not-connected rows' last-run label. */
  workflowSummaries: Map<string, WorkflowSummary>;
  /**
   * Per-trigger rollups, fetched by the page. They live up there because the
   * health filter narrows on them, and a filter can't read data owned by the
   * component it filters.
   */
  summaries: Map<string, TriggerSummary>;
  /**
   * Whether the batched summaries have landed. Until then each trigger row draws
   * its status cell as a shimmer, so the rollups load independently of the rows.
   */
  summariesLoaded: boolean;
  /** Workflows with no trigger bound, filtered by the active search. */
  unattached: WorkflowOption[];
  /** Which category sections to show, driven by the toolbar filter. */
  visibleCategories: {
    scheduled: boolean;
    webhook: boolean;
    event: boolean;
    dataset: boolean;
    standalone: boolean;
  };
  /** Whether a search is active — hides the standalone banner while searching. */
  searchActive: boolean;
  /** Called when trigger selection changes so resource actions target it. */
  onFocusTriggers?: (triggers: TriggerListItem[]) => void;
  /** Called by Delete/Backspace when trigger cards are selected. */
  onArchiveTriggers?: (triggers: TriggerListItem[]) => void;
  /** Attaches a workflow to one trigger, offered from that trigger's empty state. */
  onAddWorkflow?: (trigger: TriggerListItem) => void;
  /** Builds the hover kebab actions for each workflow row nested under a trigger. */
  workflowRowActions?: (workflow: WorkflowRowRef) => ItemAction[];
  /** Builds the kebab actions for each trigger card header (Rename / Move / Archive). */
  triggerActions?: (
    trigger: TriggerListItem,
    workflowCount: number
  ) => ItemAction[];
}> = ({
  triggers,
  onNewTrigger,
  onNewWorkflow,
  datasetNameById,
  workflowSummaries,
  summaries,
  summariesLoaded,
  unattached,
  visibleCategories,
  searchActive,
  onFocusTriggers,
  onArchiveTriggers,
  onAddWorkflow,
  workflowRowActions,
  triggerActions,
}) => {
  const scheduled = useMemo(
    () => triggers.filter((t) => t.kind === 'cron'),
    [triggers]
  );
  const webhooks = useMemo(
    () => triggers.filter((t) => t.kind === 'webhook'),
    [triggers]
  );
  const events = useMemo(
    () => triggers.filter((t) => t.kind === 'event'),
    [triggers]
  );
  const datasets = useMemo(
    () => triggers.filter((t) => t.kind === 'dataset'),
    [triggers]
  );
  const hasVisibleContent =
    (visibleCategories.scheduled && scheduled.length > 0) ||
    (visibleCategories.webhook && webhooks.length > 0) ||
    (visibleCategories.event && events.length > 0) ||
    (visibleCategories.dataset && datasets.length > 0) ||
    (visibleCategories.standalone && unattached.length > 0);
  const selectableTriggers = useMemo(
    () => [
      ...(visibleCategories.scheduled ? scheduled : []),
      ...(visibleCategories.webhook ? webhooks : []),
      ...(visibleCategories.event ? events : []),
      ...(visibleCategories.dataset ? datasets : []),
    ],
    [
      datasets,
      events,
      scheduled,
      visibleCategories.dataset,
      visibleCategories.event,
      visibleCategories.scheduled,
      visibleCategories.webhook,
      webhooks,
    ]
  );

  // Auto-open for discoverability, once per mount so the user can still collapse
  // cards. Two phases: open the first trigger immediately so the view is never
  // fully collapsed (covers a summary batch that errors and never populates),
  // then once summaries load expand every trigger that has workflows attached.
  //
  // Both phases decide the *trigger* half of the set from scratch, so they merge
  // rather than replace: picking the Standalone chip opens that group (below), but
  // triggers keep loading underneath — `standalone` never narrows the trigger
  // query — and a wholesale replace here would collapse it again a beat later.
  const [openIds, setOpenIds] = useState<string[]>([]);
  const openedWithWorkflows = useRef(false);
  const openedFirst = useRef(false);
  useEffect(() => {
    if (openedWithWorkflows.current || triggers.length === 0) {
      return;
    }
    const withStandalone = (prev: string[], triggerIds: string[]): string[] =>
      prev.includes(STANDALONE_GROUP_ID)
        ? [STANDALONE_GROUP_ID, ...triggerIds]
        : triggerIds;
    if (summaries.size > 0) {
      openedWithWorkflows.current = true;
      const withWorkflows = triggers
        .filter((t) => (summaries.get(t.id)?.workflowCount ?? 0) > 0)
        .map((t) => t.id);
      setOpenIds((prev) =>
        withStandalone(
          prev,
          withWorkflows.length > 0 ? withWorkflows : [triggers[0]!.id]
        )
      );
    } else if (!openedFirst.current) {
      openedFirst.current = true;
      setOpenIds((prev) => withStandalone(prev, [triggers[0]!.id]));
    }
  }, [triggers, summaries]);

  // The Standalone chip is a request to see them, so open the group the first time
  // becomes the only visible category. Still collapsible from there, and it
  // it becomes the only visible category. Still collapsible, and it re-arms
  // once the filter moves off Standalone again.
  const standaloneOnly =
    visibleCategories.standalone &&
    !visibleCategories.scheduled &&
    !visibleCategories.webhook &&
    !visibleCategories.event &&
    !visibleCategories.dataset;
  const openedStandalone = useRef(false);
  useEffect(() => {
    if (!standaloneOnly) {
      openedStandalone.current = false;
      return;
    }
    if (openedStandalone.current) {
      return;
    }
    openedStandalone.current = true;
    setOpenIds((prev) =>
      prev.includes(STANDALONE_GROUP_ID) ? prev : [...prev, STANDALONE_GROUP_ID]
    );
  }, [standaloneOnly]);

  // `source` is what stops a closed row from sitting there highlighted. Hovering
  // a row *targets* it — that's how Rename/Move/Archive and Delete know which
  // trigger they act on (see `onFocusTriggers`) — but targeting is not selecting,
  // and painting it left the last row the pointer happened to cross tinted
  // indefinitely. Only keyboard and range selections are painted; the pointer
  // gets the transient `hover:` tint the header already carries.
  const [selectionState, setSelectionState] = useState<{
    focusedIndex: number | null;
    selectionAnchorIndex: number | null;
    selectedIndices: number[];
    source: 'pointer' | 'keyboard';
  }>({
    focusedIndex: null,
    selectionAnchorIndex: null,
    selectedIndices: [],
    source: 'pointer',
  });
  const selectedIndexSet = useMemo(
    () => new Set(selectionState.selectedIndices),
    [selectionState.selectedIndices]
  );
  const selectedTriggers = useMemo(
    () =>
      selectionState.selectedIndices
        .map((index) => selectableTriggers[index])
        .filter((trigger): trigger is TriggerListItem => trigger !== undefined),
    [selectionState.selectedIndices, selectableTriggers]
  );

  useEffect(() => {
    const length = selectableTriggers.length;
    setSelectionState((prev) => {
      if (length === 0) {
        return {
          focusedIndex: null,
          selectionAnchorIndex: null,
          selectedIndices: [],
          source: prev.source,
        };
      }
      const focusedIndex =
        prev.focusedIndex !== null && prev.focusedIndex >= length
          ? length - 1
          : prev.focusedIndex;
      const selectionAnchorIndex =
        prev.selectionAnchorIndex !== null &&
        prev.selectionAnchorIndex >= length
          ? null
          : prev.selectionAnchorIndex;
      const selectedIndices = prev.selectedIndices.filter(
        (index) => index < length
      );
      if (
        focusedIndex === prev.focusedIndex &&
        selectionAnchorIndex === prev.selectionAnchorIndex &&
        selectedIndices.length === prev.selectedIndices.length
      ) {
        return prev;
      }
      return {
        focusedIndex,
        selectionAnchorIndex,
        selectedIndices,
        source: prev.source,
      };
    });
  }, [selectableTriggers.length]);

  useEffect(() => {
    if (selectedTriggers.length > 0) {
      onFocusTriggers?.(selectedTriggers);
    }
  }, [onFocusTriggers, selectedTriggers]);

  const focusTriggerAtIndex = useCallback(
    (index: number) => {
      const trigger = selectableTriggers[index];
      if (trigger === undefined) {
        return;
      }
      setSelectionState({
        focusedIndex: index,
        selectionAnchorIndex: index,
        selectedIndices: [index],
        source: 'pointer',
      });
      onFocusTriggers?.([trigger]);
    },
    [onFocusTriggers, selectableTriggers]
  );

  const handleArchiveSelection = useCallback(() => {
    if (selectedTriggers.length > 0) {
      onArchiveTriggers?.(selectedTriggers);
    }
  }, [onArchiveTriggers, selectedTriggers]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      const count = selectableTriggers.length;
      if (count === 0) {
        return;
      }
      const createSelectionRange = (start: number, end: number): number[] => {
        const min = Math.min(start, end);
        const max = Math.max(start, end);
        const range: number[] = [];
        for (let index = min; index <= max; index += 1) {
          range.push(index);
        }
        return range;
      };
      const moveFocus = (nextFocusedIndex: number): void => {
        setSelectionState((prev) => {
          if (e.shiftKey) {
            const anchorIndex =
              prev.selectionAnchorIndex ??
              prev.focusedIndex ??
              nextFocusedIndex;
            return {
              focusedIndex: nextFocusedIndex,
              selectionAnchorIndex: anchorIndex,
              selectedIndices: createSelectionRange(
                anchorIndex,
                nextFocusedIndex
              ),
              source: 'keyboard',
            };
          }
          return {
            focusedIndex: nextFocusedIndex,
            selectionAnchorIndex: nextFocusedIndex,
            selectedIndices: [nextFocusedIndex],
            source: 'keyboard',
          };
        });
      };

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveFocus(
          selectionState.focusedIndex === null
            ? 0
            : Math.min(selectionState.focusedIndex + 1, count - 1)
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveFocus(
          selectionState.focusedIndex === null
            ? count - 1
            : Math.max(selectionState.focusedIndex - 1, 0)
        );
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        moveFocus(0);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        moveFocus(count - 1);
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleArchiveSelection();
      }
    },
    [
      handleArchiveSelection,
      selectableTriggers.length,
      selectionState.focusedIndex,
    ]
  );

  const showStandaloneGroup =
    visibleCategories.standalone && unattached.length > 0;

  // One flat, Library-style list rather than a card per kind — `selectableTriggers`
  // is already ordered scheduled → webhook → event → dataset and narrowed to the
  // visible categories, and its index IS the selection index. Standalone is
  // as one more group; it stays *out* of `selectableTriggers` because it isn't a
  // trigger — it can't be selected, archived or moved to a folder.
  const triggerList =
    selectableTriggers.length > 0 || showStandaloneGroup ? (
      <Accordion
        type="multiple"
        value={openIds}
        onValueChange={setOpenIds}
        // Groups sit apart rather than flush. With the open-group band now
        // covering a whole group, the gap is what tells you where one block ends
        // — and it does the job the header's bottom border used to do on a
        // collapsed row, which is why that border is open-only below.
        className="w-full space-y-1"
      >
        {selectableTriggers.map((trigger, triggerIndex) => {
          const summary = summaries.get(trigger.id);
          return (
            <TriggerAccordionCard
              key={trigger.id}
              trigger={trigger}
              workflowCount={summary?.workflowCount ?? 0}
              lastFiredAt={summary?.lastFiredAt ?? null}
              workflows={summary?.workflows ?? []}
              summaryPending={!summariesLoaded && summary === undefined}
              isOpen={openIds.includes(trigger.id)}
              isSelected={
                selectionState.source === 'keyboard' &&
                selectedIndexSet.has(triggerIndex)
              }
              datasetName={
                trigger.datasetId !== null
                  ? datasetNameById?.get(trigger.datasetId) ?? null
                  : null
              }
              onFocusTrigger={() => {
                focusTriggerAtIndex(triggerIndex);
              }}
              onAddWorkflow={
                onAddWorkflow !== undefined
                  ? () => onAddWorkflow(trigger)
                  : undefined
              }
              workflowRowActions={workflowRowActions}
              triggerActions={triggerActions}
            />
          );
        })}
        {showStandaloneGroup ? (
          <StandaloneWorkflowsCard
            workflows={unattached}
            summaries={workflowSummaries}
            isOpen={openIds.includes(STANDALONE_GROUP_ID)}
            workflowRowActions={workflowRowActions}
          />
        ) : null}
      </Accordion>
    ) : null;

  return (
    <div
      className="flex flex-col pb-2 outline-none"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {!hasVisibleContent && !searchActive ? (
        <div className="flex min-h-[45dvh] items-center justify-center py-10">
          {/* Bounded panel rather than free-floating text — on a wide page a
              centered column of small text has nothing to sit in. Both halves of
              the model get a button, workflow first: a trigger with nothing
              attached can't do any work. */}
          <div className="flex max-w-md flex-col items-center rounded-xl border border-dashed border-border px-8 py-12 text-center">
            <span
              className={cn(
                'mb-5 inline-flex h-12 w-12 items-center justify-center rounded-md',
                resourceTypeIconRingClassName(ResourceType.Workflow)
              )}
            >
              <Icon icon="workflow" className="h-5 w-5" />
            </span>
            <p className="text-base font-semibold text-slate-800">
              Nothing automated yet
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              A workflow is the work; a trigger is when it runs. Start with the
              workflow — you can attach a trigger to it any time.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={onNewWorkflow}
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
              >
                <Icon icon="workflow" className="h-4 w-4" />
                New workflow
              </button>
              <button
                type="button"
                onClick={onNewTrigger}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-slate-700"
              >
                <Clock className="h-4 w-4" />
                New trigger
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {triggerList}
    </div>
  );
};
